import { Injectable, inject } from '@angular/core';

import { OddsOperations, emptyCalculatedOdds, emptyOdds } from '@frontend/sports/odds/feature';
import { SyncUserSettingsService, UserOddsFormatSettingsService } from '@frontend/sports/user/feature';
import { Store, createSelector } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import { Observable, ReplaySubject, combineLatest, debounceTime, filter, forkJoin, map, of, share, switchMap, timer } from 'rxjs';

import { StakeValidationResult } from '../../../../lib/betslip-base/stake-validation/stake-validation.models';
import { betslipSelector, selectIsLinearBetslip } from '../../../base/store/selectors';
import { IBetslipBaseState, IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { LinearTypes } from '../../../core/linear-types';
import { BetslipUnknownPick } from '../../../core/picks/betslip-unknown-pick';
import { PickId } from '../../../core/picks/pick-id';
import { PickOddsState } from '../../../core/picks/pick-models';
import { SlipTypeKeys } from '../../bet-generation/betslip-system-type';
import { BetType, BetslipGeneratorContextType, IBetslip, ICalculatorInput, ISlip } from '../../bet-generation/models';
import { BetslipGeneratorInputBuilder } from '../../bet-generation/services/betslip-generator-input-builder.service';
import { BetslipGeneratorService } from '../../bet-generation/services/betslip-generator.service';
import { SingleSlipType, SlipType } from '../../bet-generation/slip-type';
import { overAskBetsInputSelector } from '../../over-ask/selectors';
import { selectBetslipFlattenedPicksList } from '../../picks/selectors';
import { IBetslipPickState } from '../../picks/state';
import { selectQuickBetContext } from '../../quick-bet/quick-bet.selectors';
import { IQuickBetState } from '../../quick-bet/quick-bet.state';
import { IRewardToken } from '../../reward-tokens/reward-tokens.model';
import { isFreebetToken } from '../../reward-tokens/services/reward-tokens.utils';
import { IRewardTokensState } from '../../reward-tokens/state';
import { systemBetLinearSlipTypeStakesSelector } from '../../system-bet/selectors';
import { ISystemBetTypeStake } from '../../system-bet/state';
import { selectTeaserBetRewardToken, teaserBetStateSelector } from '../../teaser-bet/selectors';
import { ITeaserBetState } from '../../teaser-bet/state';
import { selectLinearTypes } from '../../types/base/selectors';
import { IBetslipTypeState } from '../../types/state';
import { IBetslipErrorsState } from '../../validation/state';
import { IDigitalSummaryViewModel } from '../components/digital/digital-summary-view.model';
import { IBetslipSummary, emptyBetslipSummary } from '../models';
import { selectStakedBetslipTypes } from '../selectors';
import { getOddsBoostWinnings } from '../utils/betslip-summary.utils';
import BetslipSummaryBuilder from './betslip-summary-builder.service';
import { BetslipSummaryCalculations } from './betslip-summary-helpers';

const summarySelector = createSelector(betslipSelector, selectBetslipFlattenedPicksList, (state, pickList) => ({
    base: state.base,
    // TODO:Amir from what I checked there is no need to select anything else besides the pickList
    //  for picks property. Maybe we can select only what is actually needed, not the whole state
    // update - konstantin: this is not easy to do because a lot of down-stream services still need the full state despite not using it
    // should be refactored but will be quite a bit of effort
    picks: { ...state.picks, pickList },
    types: state.types,
    rewardTokens: state.rewardTokens,
    errors: state.errors,
    quickBet: state.quickBet,
}));

const selectLinearTypesState = createSelector(
    selectLinearTypes,
    systemBetLinearSlipTypeStakesSelector,
    selectStakedBetslipTypes,
    (types, systemTypes, stakedTypes) => ({
        types,
        systemTypes,
        stakedTypes,
    }),
);

interface ISummaryState {
    base: IBetslipBaseState;
    picks: IBetslipPickState;
    types: IBetslipTypeState;
    rewardTokens: IRewardTokensState;
    errors: IBetslipErrorsState;
    quickBet: IQuickBetState;
}

export interface IBetslipSummaryFullViewModel {
    betslip: IBetslip;
    summary: IBetslipSummary;
    vm: IDigitalSummaryViewModel;
}

@Injectable()
export class BetslipSummaryService {
    stakeError: StakeValidationResult | null;
    private contextSummaries = new Map<BetslipGeneratorContextType, Observable<[IBetslip, IBetslipSummary]>>();
    private readonly syncUserSettingsService = inject(SyncUserSettingsService);
    private readonly userOddsFormatSettingsService = inject(UserOddsFormatSettingsService);

    constructor(
        private store: Store<IBetslipRootState>,
        private betslipGeneratorInputBuilder: BetslipGeneratorInputBuilder,
        private betslipGenerator: BetslipGeneratorService,
        private betslipSummaryBuilder: BetslipSummaryBuilder,
    ) {}

    getSummary$(summaryContextType: BetslipGeneratorContextType = BetslipGeneratorContextType.Betslip): Observable<[IBetslip, IBetslipSummary]> {
        if (this.contextSummaries.has(summaryContextType)) {
            return this.contextSummaries.get(summaryContextType)!;
        }
        switch (summaryContextType) {
            case BetslipGeneratorContextType.Betslip:
            case BetslipGeneratorContextType.EditBetSwap:
            case BetslipGeneratorContextType.AccaBoost:
            case BetslipGeneratorContextType.BetslipBar:
            case BetslipGeneratorContextType.BetBuilder:
                this.setContextSummary(summaryContextType, this.getSummaryInternal$(summaryContextType));

                return this.contextSummaries.get(summaryContextType)!;
            case BetslipGeneratorContextType.MultiReturnMessage:
                this.setContextSummary(summaryContextType, this.getMultiReturnMessageSummary$());

                return this.contextSummaries.get(summaryContextType)!;
            default:
                throw new Error(`Unknown context: ${summaryContextType}`);
        }
    }

    getQuickBetHeaderSummary$(): Observable<[IBetslip, IBetslipSummary]> {
        return this.store.select(selectQuickBetContext).pipe(
            switchMap((state) => {
                switch (state.betslipType) {
                    case BetslipType.Combo:
                        return this.getComboBetSummary$(BetslipGeneratorContextType.MultiReturnMessage);
                    case BetslipType.BetBuilder:
                        return this.getBetBuilderBetSummaryForPick$(BetslipGeneratorContextType.BetslipBar, state.pickId);
                    case BetslipType.Single:
                        return this.getSingleBetSummaryForPick$(BetslipGeneratorContextType.BetslipBar, state.pickId);
                }
            }),
        );
    }

    getQuickBetSummary$(): Observable<[IBetslip, IBetslipSummary]> {
        return this.store.select(selectQuickBetContext).pipe(
            switchMap((state) => {
                switch (state.betslipType) {
                    case BetslipType.Combo:
                        return this.getComboBetSummary$(BetslipGeneratorContextType.Betslip);
                    case BetslipType.BetBuilder:
                        return this.getBetBuilderBetSummaryForPick$(BetslipGeneratorContextType.Betslip, state.pickId);
                    case BetslipType.Single:
                        return this.getSingleBetSummaryForPick$(BetslipGeneratorContextType.Betslip, state.pickId);
                }
            }),
        );
    }

    private setContextSummary(summaryContextType: BetslipGeneratorContextType, summary$: Observable<[IBetslip, IBetslipSummary]>) {
        this.contextSummaries.set(
            summaryContextType,
            summary$.pipe(
                share({
                    connector: () => new ReplaySubject(1),
                    //Delay the destroy by 1 second per context type to avoid destructuring/reconstructing of summary observable
                    resetOnRefCountZero: () => timer(1000),
                }),
            ),
        );
    }

    selectSummaryViewModel$(summaryContextType: BetslipGeneratorContextType): Observable<IBetslipSummaryFullViewModel> {
        const isLinear$ = this.store.select(selectIsLinearBetslip);

        return isLinear$.pipe(
            switchMap((isLinear) =>
                isLinear
                    ? this.store
                          .select(selectLinearTypesState)
                          .pipe(
                              switchMap(({ types, systemTypes, stakedTypes }) =>
                                  this.getCombinedSummaryViewModel$(summaryContextType, systemTypes, types, stakedTypes),
                              ),
                          )
                    : this.getSummaryViewModel(summaryContextType),
            ),
        );
    }

    getSummaryViewModel(summaryContextType: BetslipGeneratorContextType): Observable<IBetslipSummaryFullViewModel> {
        return this.getSummary$(summaryContextType).pipe(map((summary) => this.convertSummaryToViewModel(summary)));
    }

    getCombinedSummaryViewModel$(
        summaryContextType: BetslipGeneratorContextType,
        systemSlipTypes: ISystemBetTypeStake[],
        betslipTypes: LinearTypes,
        stakedTypes: BetslipType[],
    ): Observable<IBetslipSummaryFullViewModel> {
        const summaries = Object.entries(betslipTypes)
            .filter(([key, isActive]) => isActive && stakedTypes.some((staked) => staked === key))
            .flatMap(([type]) => {
                switch (type) {
                    case BetslipType.Single:
                        return [this.getLinearSingleBetSummary$(summaryContextType)];

                    case BetslipType.Combo:
                        return [this.getComboBetSummary$(summaryContextType)];

                    case BetslipType.System:
                        return systemSlipTypes
                            .filter((systemType) => !!systemType.stake)
                            .map((systemType) => this.getSystemTypeSummary$(summaryContextType, systemType.slipType));

                    case BetslipType.Teaser:
                        return [this.getTeaserBetSummary$(summaryContextType)];

                    case BetslipType.BetBuilder:
                        return [this.getBetBuilderBetSummary$(summaryContextType)];

                    default:
                        return [];
                }
            });

        if (summaries.length === 0) {
            const betslip = {
                slips: [],
            };

            const summary = emptyBetslipSummary;

            return of({
                summary,
                betslip,
                vm: this.toViewModel(betslip, summary),
            });
        }

        return combineLatest(summaries).pipe(
            map((arr) => {
                const summary = BetslipSummaryCalculations.combineSummaryViewModels(arr);

                return this.convertSummaryToViewModel(summary);
            }),
        );
    }

    getComboBetSummaryViewModel$(summaryContextType: BetslipGeneratorContextType): Observable<IBetslipSummaryFullViewModel> {
        return this.getComboBetSummary$(summaryContextType).pipe(map((summary) => this.convertSummaryToViewModel(summary)));
    }

    private convertSummaryToViewModel(summary: [IBetslip, IBetslipSummary]): {
        betslip: IBetslip;
        summary: IBetslipSummary;
        vm: IDigitalSummaryViewModel;
    } {
        const betslip = summary[0];
        const betslipSummary = summary[1];

        return {
            betslip,
            summary: betslipSummary,
            vm: this.toViewModel(betslip, betslipSummary),
        };
    }

    getComboBetSummary$(summaryContextType: BetslipGeneratorContextType): Observable<[IBetslip, IBetslipSummary]> {
        return this.getSummaryFromDynamicInput$(summaryContextType, (state) => this.betslipGeneratorInputBuilder.getComboBetsCalculatorInput(state));
    }

    getTeaserBetSummary$(summaryContext: BetslipGeneratorContextType): Observable<[IBetslip, IBetslipSummary]> {
        return combineLatest([
            this.getBaseTeaserBetSummary$(summaryContext),
            this.store.select(teaserBetStateSelector),
            this.store.select(selectTeaserBetRewardToken),
        ]).pipe(
            map(([teaserSummary, teaserBet, token]) => {
                const betslip = teaserSummary[0];
                const betslipSummary = teaserSummary[1];

                return [betslip, this.getUpdatedTeaserBetSummary(betslipSummary ?? emptyBetslipSummary, token, teaserBet, betslip)];
            }),
        );
    }

    private getBaseTeaserBetSummary$(summaryContext: BetslipGeneratorContextType): Observable<[IBetslip, IBetslipSummary]> {
        return this.store.select(summarySelector).pipe(
            debounceTime(5),
            map((betslipState) => {
                const teaserInput = this.betslipGeneratorInputBuilder.getTeaserCalculatedInput(betslipState);
                const restrictions = this.betslipGeneratorInputBuilder.getRestrictions(summaryContext, betslipState);

                return this.betslipGenerator.calculate(teaserInput, { restrictions });
            }),
            switchMap((betslip) => {
                return this.betslipSummaryBuilder
                    .createBetslipSummary(betslip)
                    .pipe(map((betslipSummary) => [betslip, betslipSummary] as [IBetslip, IBetslipSummary]));
            }),
        );
    }

    getTeaserBetSummaryViewModel$(
        summaryContext: BetslipGeneratorContextType,
    ): Observable<{ betslip: IBetslip; summary: IBetslipSummary; vm: IDigitalSummaryViewModel }> {
        return this.getTeaserBetSummary$(summaryContext).pipe(map((summary) => this.convertSummaryToViewModel(summary)));
    }

    private getUpdatedTeaserBetSummary(
        teaserSummary: IBetslipSummary,
        token: IRewardToken | null,
        teaser: ITeaserBetState,
        betslip: IBetslip,
    ): IBetslipSummary {
        const hasErrors = (betslip.slips.length === 1 && betslip.slips[0].hasError) || teaser.placeBlocking;

        const freeBetAmount = isFreebetToken(token) ? token.amount : null;

        let teaserPrice = teaser?.teaserPrice;
        let winnings = teaserSummary.possibleWinnings;

        if (OddsOperations.isOddsEmpty(teaserSummary.totalOdds)) {
            teaserPrice = undefined;
            winnings = null;
        }

        if (winnings && teaserPrice) {
            const betTotalOdds = OddsOperations.toCalculatedOdds(teaserPrice);
            const decimalOdds = OddsOperations.toDecimalValue(betTotalOdds, this.userOddsFormatSettingsService.oddsFormat);

            //Possible winnings are calculated for teasers based on reward token amount(only free bet rewards) & stake amount.
            winnings = decimalOdds.times(Number(freeBetAmount ?? teaser.stake));
            winnings = freeBetAmount ? winnings.minus(freeBetAmount) : winnings;
        }

        const totalStake = freeBetAmount ? new Decimal(freeBetAmount) : !teaser?.stake ? null : new Decimal(teaser.stake);

        return {
            possibleWinnings: hasErrors ? null : winnings,
            possibleWinningsNet: hasErrors ? null : winnings,
            totalStake: hasErrors ? null : totalStake,
            taxation: hasErrors ? null : teaserSummary.taxation,
            betCount: teaserSummary.betCount,
            totalOdds: hasErrors ? emptyCalculatedOdds : teaser ? OddsOperations.toCalculatedOdds(teaserPrice) : teaserSummary.totalOdds,
            teaserTotalOdds: hasErrors ? emptyOdds : teaserSummary.teaserTotalOdds,
            isPotentailReturn: teaserSummary.isPotentailReturn,
            hasBetBuilder: teaserSummary.hasBetBuilder,
            averageOdds: teaserSummary.averageOdds,
            missingBetBuilderLegs: teaserSummary.missingBetBuilderLegs,
            slipSummaries: teaserSummary.slipSummaries,
        };
    }

    getSystemTypeSummary$(summaryContextType: BetslipGeneratorContextType, systemType: SlipType): Observable<[IBetslip, IBetslipSummary]> {
        return this.getSummaryFromDynamicInput$(summaryContextType, (state) =>
            this.betslipGeneratorInputBuilder.getSystemSlipsCalculatorInput(state, [systemType]),
        );
    }

    getSingleBetSummary$(summaryContextType: BetslipGeneratorContextType): Observable<[IBetslip, IBetslipSummary]> {
        return this.getSummaryFromDynamicInput$(summaryContextType, (state) => this.betslipGeneratorInputBuilder.getSingleBetsCalculatorInput(state));
    }

    getLinearSingleBetSummary$(summaryContextType: BetslipGeneratorContextType): Observable<[IBetslip, IBetslipSummary]> {
        return this.getSummaryFromDynamicInput$(summaryContextType, (state) =>
            this.betslipGeneratorInputBuilder.getLinearSingleBetsCalculatorInput(state),
        );
    }

    getSingleBetSummaryForPick$(summaryContextType: BetslipGeneratorContextType, pickId: PickId): Observable<[IBetslip, IBetslipSummary]> {
        return this.getSummaryFromDynamicInput$(summaryContextType, (state) =>
            this.betslipGeneratorInputBuilder.getSingleBetsCalculatorInputForPick(state, pickId),
        );
    }

    getBetBuilderBetSummaryForPick$(summaryContextType: BetslipGeneratorContextType, pickId: PickId): Observable<[IBetslip, IBetslipSummary]> {
        return this.getSummaryFromDynamicInput$(summaryContextType, (state) =>
            this.betslipGeneratorInputBuilder.getBetBuilderBetsCalculatorInput(state, pickId),
        );
    }

    getBetBuilderBetSummary$(summaryContextType: BetslipGeneratorContextType): Observable<[IBetslip, IBetslipSummary]> {
        return this.getSummaryFromDynamicInput$(summaryContextType, (state) =>
            this.betslipGeneratorInputBuilder.getBetBuilderBetsCalculatorInputForSummary(state),
        );
    }

    private getSummaryFromDynamicInput$(
        summaryContextType: BetslipGeneratorContextType,
        inputBuilder: (state: ISummaryState) => ICalculatorInput,
    ): Observable<[IBetslip, IBetslipSummary]> {
        return this.store.select(summarySelector).pipe(
            filter((betslipState) => !this.hasUnknownPicks(betslipState)),
            debounceTime(5),
            map((betslipState) => {
                const overrides = this.betslipGeneratorInputBuilder.getOverrides(summaryContextType, betslipState);
                const input = inputBuilder(betslipState);
                const restrictions = this.betslipGeneratorInputBuilder.getRestrictions(summaryContextType, betslipState);

                return this.betslipGenerator.calculate(input, { overrides, restrictions });
            }),
            switchMap((betslip) => {
                return this.betslipSummaryBuilder
                    .createBetslipSummary(betslip)
                    .pipe(map((betslipSummary) => [betslip, betslipSummary] as [IBetslip, IBetslipSummary]));
            }),
        );
    }

    private getSummaryInternal$(summaryContextType: BetslipGeneratorContextType): Observable<[IBetslip, IBetslipSummary]> {
        return this.getSummaryFromDynamicInput$(summaryContextType, (state) => this.betslipGeneratorInputBuilder.getCalculatorInput(state));
    }

    private getMultiReturnMessageSummary$(): Observable<[IBetslip, IBetslipSummary]> {
        return this.getComboBetSummary$(BetslipGeneratorContextType.MultiReturnMessage);
    }

    private toViewModel(betslip: IBetslip, summary: IBetslipSummary): IDigitalSummaryViewModel {
        // In digital summary possible winnings, total odds or taxation is hidden if the slip has errors ( e.g. combo prevention )
        // We make the check when only have 1 slip ( ignore multi singles )
        // For multi singles only possible winnings is null
        const hasErrors = betslip.slips.some((s) => s.hasError);
        const { boostedOdds, boostedWinnings, winningsBoost } = getOddsBoostWinnings(summary);
        const accaBoostModifications = summary.slipSummaries[0]?.rewardTokenModifications?.accaBoost;
        const hasLockedOrClosedPicks = betslip.slips.some((s: ISlip) =>
            Object.values(s.slipPicks).some((sp) => sp.pick.oddsState === PickOddsState.Locked || sp.pick.oddsState === PickOddsState.Closed),
        );

        return {
            possibleWinnings: hasErrors || hasLockedOrClosedPicks ? null : summary.possibleWinnings,
            possibleWinningsNet:
                hasErrors || hasLockedOrClosedPicks || !summary.possibleWinningsNet ? null : this.getWins(summary.possibleWinningsNet),
            totalStake:
                (hasErrors && betslip.slips.length === 1) || (hasLockedOrClosedPicks && !(betslip.slips[0].type instanceof SingleSlipType))
                    ? null
                    : summary.totalStake,
            taxation: hasErrors ? null : summary.taxation,
            totalOdds: hasErrors ? emptyCalculatedOdds : summary.totalOdds,
            baseTotalOdds: hasErrors ? undefined : summary.baseTotalOdds,
            userBetCount:
                (hasErrors && betslip.slips.length === 1) || (hasLockedOrClosedPicks && !(betslip.slips[0].type instanceof SingleSlipType))
                    ? 0
                    : this.calculateUserBetCount(summary),
            stake: this.getStake(betslip, this.syncUserSettingsService.defaultStake ?? 0),
            boostedOdds: hasErrors ? null : boostedOdds,
            boostedWinnings: hasErrors ? null : boostedWinnings,
            winningsBoost: hasErrors ? null : winningsBoost,
            originalWinnings: hasErrors ? null : (accaBoostModifications?.originalWinnings ?? null),
            isPotentailReturn: summary.isPotentailReturn || false,
        };
    }

    getWins(val: Decimal | null): Decimal {
        if (val && val?.greaterThan(0)) {
            return val;
        }

        return new Decimal(0);
    }

    private getStake(betslip: IBetslip, defaultStake: number): number | null {
        if (!betslip.slips.length) {
            return defaultStake!; //this is to return default stake when there are unchecked picks in the betslip
        }
        const stake = betslip.slips[0].stake;
        if (betslip.slips.every((slip) => slip.stake === stake)) {
            return stake;
        }

        return null;
    }

    /**
     * Calculates user display bet counts, this may not represent the real bet counts.
     * Every single slip is counted as 1 bet
     * Every combo slip is counted as 1 bet
     * Every System slip is counted as number of valid combinations it provides.
     *
     * @private
     */
    private calculateUserBetCount(summary: IBetslipSummary): number {
        let count = 0;
        for (const slipSummary of summary.slipSummaries) {
            if (slipSummary.slip.type.key === SlipTypeKeys.Single || slipSummary.slip.type.key === SlipTypeKeys.Combo) {
                count++;
            } else {
                // We want the count of all valid bets which are normal.
                // Remark: combinational XCast picks are not combinable in System, we can have only straight Forecasts combined with other straight Forecasts
                count = slipSummary.slip.bets.filter((b) => b.isValid && b.betType === BetType.Normal).length;
            }
        }

        return count;
    }

    // until a pick is resolved in CDS, the betslip's pick list contains a UnknownBetslipPick
    private hasUnknownPicks({ picks }: { picks: IBetslipPickState }): boolean {
        return picks.pickList.some((pick) => pick instanceof BetslipUnknownPick);
    }

    getOverAskSummary$(): Observable<{ original: [IBetslip, IBetslipSummary]; offer: [IBetslip, IBetslipSummary] }> {
        return this.store.select(overAskBetsInputSelector).pipe(
            map(({ bets, tokens }) => {
                const input = this.betslipGeneratorInputBuilder.getOverAskInput(bets, tokens);
                const overrides = this.betslipGeneratorInputBuilder.createOverAskCalculatorOverrides(bets);

                return [this.betslipGenerator.calculate(input), this.betslipGenerator.calculate(input, { overrides })];
            }),
            switchMap(([originalBetslip, offerBetslip]) => {
                return forkJoin([
                    this.betslipSummaryBuilder
                        .createBetslipSummary(originalBetslip)
                        .pipe(map((betslipSummary) => [originalBetslip, betslipSummary] as [IBetslip, IBetslipSummary])),
                    this.betslipSummaryBuilder
                        .createBetslipSummary(offerBetslip)
                        .pipe(map((betslipSummary) => [offerBetslip, betslipSummary] as [IBetslip, IBetslipSummary])),
                ]).pipe(map(([original, offer]) => ({ original, offer })));
            }),
        );
    }
}
